import { Alert, Stack } from '@mui/material';
import { t } from 'i18next';
import { FormEventHandler, useEffect, useState } from 'react';
import { ApiRepository } from '../../api/services/ApiRepository';
import { Dialog, DialogBody } from '../../common/components/Modal/Modal';
import { getErrorMessage } from '../../common/errors/errors';
import { Interpolation } from '../../config/i18n/components/Interpolation';
import { ButtonBox, DangerButton, PrimaryButton, TertiaryButton } from '../components/Buttons';
import { CopyPassword } from '../components/CopyPassword';
import { AppConfigContext } from '../../common/services/AppConfigContext';

type Props = {
  onClosed: (key: string | undefined) => void;
};

export const ResetApiKeyFlow = ({ onClosed }: Props) => {
  const [apiService] = useState(new ApiRepository());
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [apiKey, setApiKey] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      apiService.cancelAllRequests();
    };
  }, [apiService]);

  const onSubmit: FormEventHandler = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      const apiKey = await apiService.resetKey();

      // Store the new key immediately after reset
      AppConfigContext.setToken(apiKey, AppConfigContext.getPresets().sessionDuration);

      setLoading(false);
      setApiKey(apiKey);
    } catch (e: unknown) {
      setError(getErrorMessage(e));
      setLoading(false);
    }
  };

  return (
    <Dialog title={t('resetApiKey.title')} onDismiss={() => onClosed(apiKey ?? undefined)}>
      <DialogBody>
        <Stack spacing={1.5} component="form" onSubmit={onSubmit}>
          {error && <Alert severity="error">{error}</Alert>}
          {apiKey === null && (
            <>
              <Interpolation i18nKey="resetApiKey.info.content" sx={{ color: 'grey.700' }} />
              <ButtonBox>
                <TertiaryButton disabled={loading} onClick={() => onClosed(undefined)}>
                  {t('resetApiKey.info.back')}
                </TertiaryButton>
                <DangerButton autoFocus disabled={loading} type="submit">
                  {t('resetApiKey.info.submit')}
                </DangerButton>
              </ButtonBox>
            </>
          )}
          {apiKey !== null && (
            <>
              <Interpolation i18nKey="resetApiKey.success.content" sx={{ color: 'grey.700' }} />
              <CopyPassword password={apiKey} size="large" />
              <ButtonBox>
                <PrimaryButton onClick={() => onClosed(apiKey)}>{t('resetApiKey.success.close')}</PrimaryButton>
              </ButtonBox>
            </>
          )}
        </Stack>
      </DialogBody>
    </Dialog>
  );
};
